
import { api } from '../core';
import { AgentProfile } from '../../types';
import { MOCK_AGENTS } from '../mockData';
import { localDB } from '../helpers';

// Map API AgentOutputVM to UI AgentProfile
const mapToUI = (apiAgent: any): AgentProfile => {
    let params = {};
    try { params = JSON.parse(apiAgent.parameters || '{}'); } catch(e) {}

    return {
        id: apiAgent.id,
        name: apiAgent.name,
        role: apiAgent.description || 'Agent',
        avatar: (params as any).avatar || 'fa-robot',
        dialect: (params as any).dialect || 'najdi',
        voice: (params as any).voice || 'Kore',
        systemPrompt: apiAgent.systemPrompt || '',
        autoReply: apiAgent.isActive,
        voiceOutput: (params as any).voiceOutput ?? true,
        sensitivity: (params as any).sensitivity ?? 0.5,
        stats: {
            interactions: apiAgent.usageCount || 0,
            lastActive: apiAgent.lastUsedAt || '-'
        },
        apiKey: (params as any).apiKey,
        endpointUrl: (params as any).endpointUrl
    };
};

// Map UI AgentProfile to API AgentCreateVM
const mapToAPI = (agent: Partial<AgentProfile>) => {
    const params = {
        dialect: agent.dialect,
        voice: agent.voice,
        avatar: agent.avatar,
        voiceOutput: agent.voiceOutput,
        sensitivity: agent.sensitivity,
        apiKey: agent.apiKey,
        endpointUrl: agent.endpointUrl
    };

    return {
        name: agent.name,
        description: agent.role,
        systemPrompt: agent.systemPrompt,
        parameters: JSON.stringify(params),
        isActive: agent.autoReply,
        isPublic: false,
        model: 'gemini-pro', // Default
        provider: 'google'
    };
};

export const AgentsService = {
  getAll: async (): Promise<AgentProfile[]> => {
    return api.execute<AgentProfile[]>(
      '/api/V1/Agent/GetAllByLanguage',
      async () => localDB.get<AgentProfile[]>('lahja_agents', MOCK_AGENTS),
      { mockDelay: 600 }
    ).then(res => {
        if (Array.isArray(res) && res.length > 0 && 'systemPrompt' in res[0]) {
            return res.map(mapToUI);
        }
        return res as AgentProfile[];
    });
  },

  update: async (agent: AgentProfile): Promise<AgentProfile> => {
    return api.execute<AgentProfile>(
      `/api/V1/Agent/Update/${agent.id}`,
      async () => {
        localDB.updateItem('lahja_agents', agent, []);
        return agent;
      },
      // API expects { id: "...", body: { ...AgentCreateVM... } }
      { method: 'PUT', body: { id: agent.id, body: mapToAPI(agent) } }
    ).then(res => ('systemPrompt' in (res as any) ? mapToUI(res) : res));
  },

  create: async (agent: AgentProfile): Promise<AgentProfile> => {
    const newAgentLocal = { ...agent, id: Date.now().toString() };
    return api.execute<AgentProfile>(
      '/api/V1/Agent/Create',
      async () => {
        localDB.updateItem('lahja_agents', newAgentLocal, []); 
        return newAgentLocal;
      },
      { method: 'POST', body: mapToAPI(agent) }
    ).then(res => ('systemPrompt' in (res as any) ? mapToUI(res) : res));
  },

  delete: async (id: string): Promise<boolean> => {
    return api.execute<boolean>(
      `/api/V1/Agent/Delete/${id}`,
      async () => {
        localDB.deleteItem('lahja_agents', id, []);
        return true;
      },
      { method: 'DELETE' }
    );
  }
};
